import {
  AssignmentRow,
  AssignmentSearchRecord,
  DSECapacityMetrics,
  EngagementDetailsRecord,
  EngagementRow,
} from '../../../contracts/DSEContracts';

const MS_PER_DAY = 1000 * 60 * 60 * 24;

const round2 = (n: number): number => Math.round(n * 100) / 100;

const isValidDate = (date: Date): boolean => !isNaN(date.getTime());

const numberOrZero = (value: number | undefined): number =>
  typeof value === 'number' && isFinite(value) ? value : 0;

/**
 * Map a raw GRM search-index assignment document to a DSE Assignments-table row.
 *
 * Behavior-parity port of `dseModal.controller.js:90-159` (GRM Experience):
 *   - annualizes allocated hours over the request duration (inclusive day count),
 *   - `isSigned` when Signed === 'Yes' OR Tpid is a real value (!= 'Blank'),
 *   - `isActive` unless the assignment ended more than one day ago.
 * Returns null when hours/dates are missing (source skips such records).
 */
export function mapAssignmentRow(
  record: AssignmentSearchRecord,
  now: Date = new Date()
): AssignmentRow | null {
  if (
    record.RequestedDurationInHours === undefined ||
    record.RequestedStartDate === undefined ||
    record.RequestedEndDate === undefined
  ) {
    return null;
  }

  const sDate = new Date(record.RequestedStartDate);
  const eDate = new Date(record.RequestedEndDate);
  if (!isValidDate(sDate) || !isValidDate(eDate)) {
    return null;
  }

  const startUtc = Date.UTC(sDate.getFullYear(), sDate.getMonth(), sDate.getDate());
  const endUtc = Date.UTC(eDate.getFullYear(), eDate.getMonth(), eDate.getDate());
  const dayDifference = Math.floor((endUtc - startUtc) / MS_PER_DAY) + 1;
  if (dayDifference <= 0) {
    return null;
  }

  let remainingDays = Math.round((eDate.getTime() - now.getTime()) / MS_PER_DAY);
  if (remainingDays < 0) {
    remainingDays = 0;
  }

  const isSigned =
    (record.Signed !== undefined && record.Signed === 'Yes') ||
    (record.Tpid !== undefined && record.Tpid !== 'Blank');

  const nowMinusEnd = now.getTime() - eDate.getTime();
  const isActive = nowMinusEnd > 0 ? nowMinusEnd <= MS_PER_DAY : true;

  return {
    isSigned,
    assignmentName: record.ProjectName ?? '',
    allocHours: record.RequestedDurationInHours,
    allocHoursAnnual: Math.round(
      (record.RequestedDurationInHours / dayDifference) * 365
    ),
    perSold: 'N/A',
    role: record.Role ?? '',
    startDate: sDate.toLocaleDateString('en-us'),
    endDate: eDate.toLocaleDateString('en-us'),
    duration: dayDifference,
    remainingDays,
    deliveryStatus: record.ResourceRequestStatusEnum ?? '',
    country: record.DeliveryCountryName ?? '',
    demandSourceId: record.DemandSourceId ?? '',
    isActive,
    projectResourceManager: record.ProjectResourceManager ?? '',
  };
}

/** Map all valid assignment documents while preserving the legacy one-day activity grace. */
export function mapAssignmentRows(
  records: AssignmentSearchRecord[],
  now: Date = new Date()
): AssignmentRow[] {
  const rows: AssignmentRow[] = [];
  records.forEach((record) => {
    const row = mapAssignmentRow(record, now);
    if (row) {
      rows.push(row);
    }
  });
  return rows;
}

export interface EngagementMappingResult {
  assignments: AssignmentRow[];
  engagements: EngagementRow[];
}

/**
 * Merge PartnerIntegration engagement details into assignment rows.
 *
 * Engagement activity intentionally has no one-day grace (`end >= now`), unlike assignments.
 * `% sold` follows the legacy `HoursSold / allocated hours` calculation, with a safe 0 result
 * when allocated hours is 0 instead of the legacy `Infinity`.
 */
export function mapEngagementDetails(
  assignmentRows: AssignmentRow[],
  records: EngagementDetailsRecord[],
  now: Date = new Date()
): EngagementMappingResult {
  const assignmentContext: Record<
    string,
    { owner: string; district: string }
  > = {};
  assignmentRows.forEach((row) => {
    if (row.demandSourceId && !assignmentContext[row.demandSourceId]) {
      assignmentContext[row.demandSourceId] = {
        owner: row.projectResourceManager ?? '',
        district: row.country,
      };
    }
  });

  let assignments = assignmentRows.slice();
  const engagements: EngagementRow[] = [];

  records.forEach((record) => {
    const demandSourceId = record.DemandSourceId ?? '';
    const context = assignmentContext[demandSourceId];
    if (!context) {
      return;
    }

    const customer = record.Customer ?? '';
    const hoursSold = numberOrZero(record.HoursSold);
    const start = record.StartDate ? new Date(record.StartDate) : undefined;
    const end = record.EndDate ? new Date(record.EndDate) : undefined;
    const validStart = start && isValidDate(start) ? start : undefined;
    const validEnd = end && isValidDate(end) ? end : undefined;

    engagements.push({
      demandSourceId,
      title: validStart
        ? `${customer}|${validStart.getFullYear()}|${hoursSold}`
        : `${customer}|${hoursSold}`,
      customer,
      scheduleId: record.ScheduleId ?? '',
      scheduleName: record.ScheduleName ?? '',
      hoursSold,
      hoursConsumed: numberOrZero(record.HoursConsumed),
      startDate: validStart
        ? validStart.toLocaleDateString('en-us')
        : '',
      endDate: validEnd ? validEnd.toLocaleDateString('en-us') : '',
      isActive: validEnd ? validEnd.getTime() >= now.getTime() : false,
      tamAlias: record.TamAlias ?? '',
      owner: context.owner,
      district: context.district,
      deliveryRate: numberOrZero(record.DeliveryRate),
      remainingDays: numberOrZero(record.RemainingDays),
      contractId: record.ContractId ?? '',
    });

    assignments = assignments.map((row) =>
      row.demandSourceId === demandSourceId
        ? {
            ...row,
            perSold:
              row.allocHours === 0
                ? 0
                : Math.round((hoursSold / row.allocHours) * 100),
          }
        : row
    );
  });

  return { assignments, engagements };
}

/**
 * Compute DSE capacity metrics from mapped rows + annual contract hours.
 *
 * Parity port of the accumulation loop (`:141-151`) + `updateMetrics` (`:197-213`):
 * only ACTIVE assignments contribute; signed => committed, else forecasted (annualized);
 * `%committed`/`%planned` are annual ratios vs contract hours (2 dp), guarded for 0.
 */
export function computeCapacityMetrics(
  rows: AssignmentRow[],
  contractHours: number
): DSECapacityMetrics {
  let committed = 0;
  let forecasted = 0;
  let committedAnnual = 0;
  let forecastedAnnual = 0;

  for (const row of rows) {
    if (!row.isActive) {
      continue;
    }
    const annual =
      row.duration > 0
        ? Math.round((row.allocHours * 365) / row.duration)
        : 0;
    if (row.isSigned) {
      committed += row.allocHours;
      committedAnnual += annual;
    } else {
      forecasted += row.allocHours;
      forecastedAnnual += annual;
    }
  }

  const uncommitted = contractHours - committedAnnual;
  const unplanned = contractHours - (forecastedAnnual + committedAnnual);

  let perCommitted = 0;
  let perPlanned = 0;
  if (contractHours > 0) {
    perCommitted = round2((committedAnnual / contractHours) * 100);
    perPlanned = round2(((forecastedAnnual + committedAnnual) / contractHours) * 100);
  }

  return {
    committed,
    forecasted,
    committedAnnual,
    forecastedAnnual,
    uncommitted,
    unplanned,
    perCommitted,
    perPlanned,
  };
}

/** Filter mapped assignment rows by Active/Historic + a free-text term (parity: source search fields). */
export function filterAssignments(
  rows: AssignmentRow[],
  view: 'Active' | 'Historic',
  searchTerm: string
): AssignmentRow[] {
  const byView = rows.filter((r) => r.isActive === (view === 'Active'));
  const term = (searchTerm || '').trim().toLowerCase();
  if (!term) {
    return byView;
  }
  return byView.filter(
    (r) =>
      r.assignmentName.toLowerCase().includes(term) ||
      r.role.toLowerCase().includes(term) ||
      r.deliveryStatus.toLowerCase().includes(term) ||
      r.country.toLowerCase().includes(term)
  );
}

/** Filter mapped engagement rows by Active/Historic + the legacy searchable fields. */
export function filterEngagements(
  rows: EngagementRow[],
  view: 'Active' | 'Historic',
  searchTerm: string
): EngagementRow[] {
  const byView = rows.filter((row) => row.isActive === (view === 'Active'));
  const term = (searchTerm || '').trim().toLowerCase();
  if (!term) {
    return byView;
  }
  return byView.filter(
    (row) =>
      row.title.toLowerCase().includes(term) ||
      row.scheduleId.toLowerCase().includes(term) ||
      row.scheduleName.toLowerCase().includes(term) ||
      row.customer.toLowerCase().includes(term) ||
      row.owner.toLowerCase().includes(term) ||
      row.district.toLowerCase().includes(term) ||
      row.tamAlias.toLowerCase().includes(term) ||
      row.contractId.toLowerCase().includes(term)
  );
}
